import { create } from 'zustand';

export type ToastTone = 'ok' | 'info' | 'warn' | 'alert';

export interface ToastAction {
  /** Etichetta del bottone azione (es. "Annulla"). */
  label: string;
  /** Callback invocata al click. Il toast viene chiuso subito dopo. */
  onClick: () => void;
}

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Azione opzionale inline (pattern Undo alla Gmail). */
  action?: ToastAction;
}

/** Opzioni estese passabili a `toast.*`. */
export interface ToastOptions {
  description?: string;
  /** Azione inline (es. Annulla). Quando presente, la durata di default sale a 6s. */
  action?: ToastAction;
  /** Durata custom in ms. Override del default (4.2s, o 6s se c'è un'azione). */
  duration?: number;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>, duration?: number) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_MS = 4200;
const ACTION_MS = 6000;

const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t, duration) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));
    const ms = duration ?? (t.action ? ACTION_MS : DEFAULT_MS);
    const handle = setTimeout(() => {
      timers.delete(id);
      set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
    }, ms);
    timers.set(id, handle);
    return id;
  },
  dismiss: (id) => {
    const h = timers.get(id);
    if (h) { clearTimeout(h); timers.delete(id); }
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => {
    timers.forEach((h) => clearTimeout(h));
    timers.clear();
    set({ toasts: [] });
  },
}));

/** Normalizza la firma legacy (string) e quella nuova (ToastOptions). */
function normalize(arg?: string | ToastOptions): { description?: string; action?: ToastAction; duration?: number } {
  if (arg == null) return {};
  if (typeof arg === 'string') return { description: arg };
  return arg;
}

function make(tone: ToastTone) {
  return (title: string, arg?: string | ToastOptions) => {
    const { description, action, duration } = normalize(arg);
    return useToastStore.getState().push({ tone, title, description, action }, duration);
  };
}

export const toast = {
  ok: make('ok'),
  info: make('info'),
  warn: make('warn'),
  alert: make('alert'),
  /**
   * Toast con azione "Annulla" (pattern Gmail): esegui SUBITO l'effetto ottimistico,
   * poi chiama questo. Se l'utente preme Annulla, viene invocato `onUndo` e il commit
   * differito NON parte; altrimenti dopo `duration` (default 6s) parte `commit`.
   */
  undo: (title: string, opts: { onUndo: () => void; commit?: () => void; description?: string; duration?: number }) => {
    const dismiss = useToastStore.getState().dismiss;
    let undone = false;
    const id = useToastStore.getState().push(
      {
        tone: 'info',
        title,
        description: opts.description,
        action: {
          label: 'Annulla',
          onClick: () => { undone = true; opts.onUndo(); dismiss(id); },
        },
      },
      opts.duration,
    );
    if (opts.commit) {
      setTimeout(() => { if (!undone) opts.commit!(); }, opts.duration ?? ACTION_MS);
    }
    return id;
  },
};
